/**
 * Built-in effect `shockwave` (W2-01b; P§5.2): an expanding ring that distorts the picture; animate `progress`.
 * Reimplemented from the behaviour (badged approximate).
 *
 * Distances from (`centerX`, `centerY`) are measured with the screen's aspect ratio (the ring is round; 1 = the
 * screen height) and the ring's radius is `progress`. Inside the band |d - progress| <= `width`, a pixel reads the
 * screen displaced along the radius by diff * (1 - |diff * expand| ^ distortion), diff = d - progress: zero on the
 * ring's crest and at its edges, strongest in between, pushing outward outside the crest and inward inside it.
 */

import { m } from './builtinFx.messages';
import { defineBuiltin, SAMPLE_SCREEN } from './shared';

export const shockwave = defineBuiltin({
  name: 'shockwave',
  label: { key: m.shockwave },
  description: { key: m.shockwaveDesc },
  inputs: ['screenSize'],
  uniforms: [
    {
      name: 'progress',
      type: 'float',
      default: 0.2,
      hint: '0..1',
      min: 0,
      max: 1,
      label: { key: m.shockwaveProgress },
    },
    { name: 'centerX', type: 'float', default: 0.5, hint: '0..1', min: 0, max: 1, label: { key: m.shockwaveCenterX } },
    { name: 'centerY', type: 'float', default: 0.5, hint: '0..1', min: 0, max: 1, label: { key: m.shockwaveCenterY } },
    { name: 'width', type: 'float', default: 0.1, min: 0, max: 0.5, label: { key: m.shockwaveWidth } },
    { name: 'distortion', type: 'float', default: 0.8, min: 0, max: 4, label: { key: m.shockwaveDistortion } },
    { name: 'expand', type: 'float', default: 10, min: 0, max: 50, label: { key: m.shockwaveExpand } },
  ],
  helpers: [SAMPLE_SCREEN],
  main: `
void main() {
  vec2 aspect = vec2(screenSize.x / max(screenSize.y, 1.0), 1.0);
  vec2 d = (uv - vec2(centerX, centerY)) * aspect;
  float dist = length(d);
  float diff = dist - progress;
  vec2 p = uv;
  if (abs(diff) <= width && dist > 0.0) {
    float k = 1.0 - pow(max(abs(diff * expand), 1e-6), distortion);
    p = uv + (d / dist) * (diff * k) / aspect;
  }
  gl_FragColor = sampleScreen(p);
}`,
});
